import { isAxiosError, type AxiosError } from 'axios';

type ErrorCodeResponseMap = {
  /**
   * Indicates that one or more requested resources were not found.
   */
  40402: {
    dimensions?: string[];
    events?: string[];
    aggregations?: string[];
  };
  /**
   * Indicates that a funnel dashboard with the same unique name already exists.
   */
  40005: unknown;
  /**
   * Indicates that the step-specific propagation settings are invalid, due to out-of-range pattern steps length.
   */
  40010: unknown;
};

type ErrorCode = keyof ErrorCodeResponseMap;

interface QueryErrorResponse<Code extends number = number> {
  code: Code;
  message: string;
  data: Code extends ErrorCode ? ErrorCodeResponseMap[Code] : unknown;
  error?: unknown;
}

type QueryError<Code extends number = number> = AxiosError<QueryErrorResponse<Code>> & {
  response: NonNullable<AxiosError<QueryErrorResponse<Code>>['response']>;
};

export function isQueryError(payload: unknown): payload is QueryError;
export function isQueryError<C extends ErrorCode>(
  payload: unknown,
  errorCode: C,
): payload is QueryError<C>;
export function isQueryError(payload: unknown, errorCode?: ErrorCode): payload is QueryError {
  if (!isAxiosError(payload)) return false;

  const { response } = payload;

  if (!response?.data) return false;

  if (errorCode === undefined) {
    return true;
  }

  return (response.data as QueryErrorResponse).code === errorCode;
}
